/**
 * MenuStatsBar — the menu's totals, pinned to the BOTTOM of the builder.
 * The one and only totals surface in the app (the PDF is the opposite:
 * totals at the top). Recomputed on every amount keystroke.
 *
 * Shows `current / goal` per macro with a 4px progress track. The NUMBER is the
 * true percentage; the BAR is clamped to 100 — doof clamped both, so its bar
 * could never turn red once the client went over the goal.
 *
 * When the client has no `nutrition.goals` the bar renders totals only: no
 * tracks, no percentages, never a 0-goal division.
 */

import { calcGoalProgress } from "../../../utils/nutritionCalc";
import HoverTip from "../../ui/HoverTip";
import MacroIcon from "../MacroIcon";
import { MACROS, fmtNum } from "./IngredientRow";
import "../../../pages/dashboard/nutrition/MenuBuilderPage.css";

const STATUS_CLASS = {
  under: "under",
  on_target: "on-target",
  over: "over",
};

const MenuStatsBar = ({ totals, goals, isAveragesMenu }) => {
  const hasGoals = MACROS.some((macro) => Number(goals?.[macro.key]) > 0);

  const titleTip = [
    isAveragesMenu
      ? "תפריט ממוצעים: התת-בלוקים הם אפשרויות חלופיות, ולכן הערך היומי הוא הסכום מחולק במספר התת-בלוקים"
      : "סכום כל הבלוקים בתפריט — יום אכילה אחד מלא",
    hasGoals
      ? "המספר מוצג מול היעד היומי של הלקוח/ה; האחוז הוא היחס האמיתי גם מעל 100%"
      : "כשיוגדרו יעדים ללקוח/ה, כל ערך יוצג כאן מול היעד שלו",
  ].join(". ");

  return (
    <footer className="nmb-stats" role="region" aria-label="סיכום ערכי התפריט">
      <div className="nmb-stats-head">
        <HoverTip text={titleTip}>
          <span className="nmb-stats-title" tabIndex={0}>
            {isAveragesMenu ? "ממוצע ערכים יומי" : 'סה"כ ערכים יומיים'}
          </span>
        </HoverTip>
        <span className="nmb-stats-note">
          {hasGoals ? "מתעדכן בזמן אמת מול היעדים של הלקוח/ה" : "לא הוגדרו יעדים ללקוח/ה — סיכום בלבד"}
          {isAveragesMenu ? " · תפריט ממוצעים" : ""}
        </span>
      </div>

      <div className="nmb-stats-grid">
        {MACROS.map((macro) => {
          const actual = Number(totals?.[macro.key]) || 0;
          const goal = Number(goals?.[macro.key]) || 0;
          const progress = calcGoalProgress(actual, goal);
          const statusClass = STATUS_CLASS[progress.status] || "";

          return (
            <div className={`nmb-stat ${statusClass}`} key={macro.key}>
              <span className="nmb-stat-label">
                <MacroIcon macro={macro.key} size={16} />
                {macro.label}
              </span>
              <span className="nmb-stat-value">
                <strong>{fmtNum(actual)}</strong>
                {progress.hasGoal && <span className="nmb-stat-goal">/ {fmtNum(goal)}</span>}
                <span className="nmb-stat-unit">{macro.unit}</span>
              </span>

              {progress.hasGoal ? (
                <>
                  <span
                    className="nmb-stat-track"
                    role="progressbar"
                    aria-valuenow={progress.percent}
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-label={`${macro.label} ביחס ליעד`}
                  >
                    <span
                      className="nmb-stat-fill"
                      style={{ width: `${progress.barPercent}%` }}
                    />
                  </span>
                  <span className="nmb-stat-percent">{progress.percent}% מהיעד</span>
                </>
              ) : null}
            </div>
          );
        })}
      </div>
    </footer>
  );
};

export default MenuStatsBar;
